import { Component, OnInit, Inject } from '@angular/core';
import { LovDataService, LanguageService, CompetitionService, ECaseSnackBarService } from 'synto-common';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ParametersService, ECaseDateFormatterPipe } from 'synto-common';
import moment from 'moment';
import 'moment-timezone';

@Component({
  selector: 'app-general-directory',
  templateUrl: './general-directory.component.html',
  styleUrls: ['./general-directory.component.scss']
})
export class GeneralDirectoryComponent implements OnInit {
  selectedLang: string;
  taxonomyTerms: any;
  isMobileView = false;
  languages: any[];

  facultyLabel: string = 'Faculty';
  programLabel: string = 'Program of Study';
  levelLabel: string = 'Level of Study';
  stageLabel: string = 'Stage of Study';
  legalStatusLabel: string = 'Legal Status';
  applicationRequirementLabel: string = 'Application Requirement';
  residencyLabel: string = 'Canadian Residency';
  accessLabel: string = 'Access and Inclusion';
  miscLabel: string = 'Misc Categories';

  facultyLabelTooltip: string = '';
  programLabelTooltip: string = '';
  levelLabelTooltip: string = '';
  stageLabelTooltip: string = '';
  legalStatusLabelTooltip: string = '';
  applicationRequirementLabelTooltip: string = '';
  residencyLabelTooltip: string = '';
  accessLabelTooltip: string = '';
  miscLabelTooltip: string = '';

  faculties = [];
  allPrograms = [];
  filteredPrograms = [];
  levels = [];
  stages = [];
  legalStatuses = [];
  applicationRequirements = [];
  residencies = [];

  accessOptions = [];
  miscOptions = [];

  displayedColumns: string[] = ['awardName', 'competition', 'openDate', 'deadline', 'details'];

  competitions = [];
  totalCount = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];
  loading = false;
  filtersLoading = false;

  sortBy = 'programName';
  sortOrder = 'asc';

  mobileSortOptions = [
    { value: 'programName', label: 'ecase.opportunity.programName', direction: 'asc' },
    { value: 'programName', label: 'ecase.opportunity.programName', direction: 'desc' },
    { value: 'competitionName', label: 'ecase.common.generalDirectory.competitionName', direction: 'asc' },
    { value: 'competitionName', label: 'ecase.common.generalDirectory.competitionName', direction: 'desc' },
    { value: 'openDate', label: 'ecase.opportunity.accessDate', direction: 'asc' },
    { value: 'openDate', label: 'ecase.opportunity.accessDate', direction: 'desc' },
    { value: 'closeDate', label: 'ecase.opportunity.deadlineForSubmission', direction: 'asc' },
    { value: 'closeDate', label: 'ecase.opportunity.deadlineForSubmission', direction: 'desc' }
  ];

  selectedTagIds: number[] = [];
  isFilterApplied = false;
  isFilterPanelExpanded = false;
  pendingFilterChanges = false;
  originalAppliedTagIds: number[] = [];
  originalAppliedSearchText: string = '';
  isMobileFiltersExpanded = false;
  actualViewportHeight = 0;
  isSafari = false;

  form: FormGroup;
  searchAwardNameOrId: string = '';

  constructor(
    private lovDataService: LovDataService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private competitionService: CompetitionService,
    private formBuilder: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private eCaseSnackBarService: ECaseSnackBarService,
    @Inject(ECaseDateFormatterPipe) private eCaseDateFormatterPipe: ECaseDateFormatterPipe,
    private parameterService: ParametersService
  ) {}

  ngOnInit(): void {
    this.languageService.getAllLanguages().subscribe((data) => {
      this.languages = this.languageService.createLanguage(data);
      this.selectedLang = this.translate.getDefaultLang();
    });

    this.translate.onLangChange.subscribe((params: LangChangeEvent) => {
      this.selectedLang = params.lang;
      this.updateAllFields();
      this.updateCompetitionsLanguage();
    });

    this.filtersLoading = true;
    this.lovDataService.getTermsAndMetadataByTaxonomyId(160).subscribe(
      (response) => {
        const actualResponse = response.data || response;
        this.taxonomyTerms = actualResponse;
        this.populateAllFields();
        this.filtersLoading = false;
      },
      (error) => {
        console.error('API Error:', error);
        this.filtersLoading = false;
      }
    );

    const width = window.innerWidth;
    const height = window.innerHeight;
    this.isMobileView = width < 1200;
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    this.actualViewportHeight = window.innerHeight;
    console.log('Initial view mode set:', {
      isMobileView: this.isMobileView,
      width,
      height,
      isSafari: this.isSafari,
      actualViewportHeight: this.actualViewportHeight
    });


    this.loadCompetitions();

    this.form = this.formBuilder.group({
      faculty: [[]],
      program: [[]],
      level: [[]],
      stage: [[]],
      legalStatus: [[]],
      applicationRequirement: [[]],
      residency: [[]],
      searchAwardNameOrId: ['']
    });

    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Web
    ]).subscribe((result) => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      this.isMobileView = screenWidth < 1200;
    });

    window.addEventListener('resize', () => {
      const resizeWidth = window.innerWidth;
      const resizeHeight = window.innerHeight;
      const shouldBeMobile = resizeWidth < 1200;
      this.actualViewportHeight = resizeHeight;

      if (this.isMobileView !== shouldBeMobile) {
        this.isMobileView = shouldBeMobile;
        if (!shouldBeMobile) {
          this.closeMobileFilters();
        }
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isMobileFiltersExpanded) {
        this.closeMobileFilters();
      }
    });
  }

  loadCompetitions(): void {
    console.log('Starting to load competitions, setting loading to true');
    this.loading = true;
    const requestObj = {
      'languageCode': this.selectedLang,
      'pageNumber': this.currentPage,
      'pageSize': this.pageSize,
      'sortBy': this.sortBy,
      'sortOrder': this.sortOrder,
      'filterProgramName': this.searchAwardNameOrId.trim() || '',
      'tagIds': this.selectedTagIds
    };

    console.log('Request object for competitions:', requestObj);

    this.competitionService.getActiveCompetitionsForGeneralDirectory(requestObj).subscribe({
      next: (response) => {
        console.log('Competitions API Response received');
        if (response && response.response) {
          this.competitions = response.response;
          this.totalCount = response.totalCount || 0;
          this.updateCompetitionsLanguage();
        }
        console.log('Setting loading to false');
        this.loading = false;
      },
      error: (error) => {
        console.error('Competitions API Error:', error);
        console.log('Setting loading to false due to error');
        this.loading = false;
      }
    });
  }

  onPageChange(event: any): void {
    console.log('Page change event:', event);
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCompetitions();
  }

  onSortChange(event: any): void {
    console.log('Sort change event:', event);

    const sortFieldMap: { [key: string]: string } = {
      'awardName': 'programName',
      'competition': 'competitionName',
      'openDate': 'openDate',
      'deadline': 'closeDate'
    };

    const columnName = event.active;
    const newSortBy = sortFieldMap[columnName] || 'programName';
    const newSortOrder = event.direction || 'asc';

    if (this.sortBy !== newSortBy || this.sortOrder !== newSortOrder) {
      this.sortBy = newSortBy;
      this.sortOrder = newSortOrder;
      this.currentPage = 0;
      this.loadCompetitions();
    }
  }

  onMobileSortChange(event: any): void {
    console.log('Mobile sort change event:', event);
    const selectedOption = event.value;

    if (selectedOption) {
      this.sortBy = selectedOption.value;
      this.sortOrder = selectedOption.direction;
      this.currentPage = 0;
      this.loadCompetitions();
    }
  }

  get selectedSortOption(): any {
    return this.mobileSortOptions.find(opt => opt.value === this.sortBy && opt.direction === this.sortOrder);
  }

  onFilterChange(event: any, filterType: string): void {
    console.log('Filter change event:', event, 'Filter type:', filterType);

    const selectedValues = event.value;

    this.removeExistingFilterValues(filterType);

    if (selectedValues && selectedValues.length > 0) {
      this.selectedTagIds.push(...selectedValues);
      console.log('Added tag IDs:', selectedValues, 'Current selectedTagIds:', this.selectedTagIds);
    }

    if (filterType === 'faculty') {
      this.updateProgramOptions();
    }

    this.pendingFilterChanges = true;
  }

  onCheckboxChange(event: any, optionId: number): void {
    console.log('Checkbox change event:', event, 'Option ID:', optionId);

    if (event.checked) {
      if (!this.selectedTagIds.includes(optionId)) {
        this.selectedTagIds.push(optionId);
        console.log('Added checkbox tag ID:', optionId, 'Current selectedTagIds:', this.selectedTagIds);
      }
    } else {
      const index = this.selectedTagIds.indexOf(optionId);
      if (index > -1) {
        this.selectedTagIds.splice(index, 1);
        console.log('Removed checkbox tag ID:', optionId, 'Current selectedTagIds:', this.selectedTagIds);
      }
    }

    this.pendingFilterChanges = true;
  }

  applyFilters(): void {
    console.log('Applying filters with tagIds:', this.selectedTagIds);
    this.searchAwardNameOrId = this.searchAwardNameOrId.trim();
    this.form.get('searchAwardNameOrId').setValue(this.searchAwardNameOrId);

    this.isFilterApplied = true;
    this.pendingFilterChanges = false;
    this.originalAppliedTagIds = [...this.selectedTagIds];
    this.originalAppliedSearchText = this.searchAwardNameOrId;
    this.isFilterPanelExpanded = false;
    this.currentPage = 0;
    this.loadCompetitions();
    this.closeMobileFilters();
  }

  clearFilters(): void {
    console.log('Clearing all filters');
    this.selectedTagIds = [];
    this.isFilterApplied = false;
    this.pendingFilterChanges = false;
    this.originalAppliedTagIds = [];
    this.originalAppliedSearchText = '';
    this.isFilterPanelExpanded = false;
    this.currentPage = 0;

    this.form.patchValue({
      faculty: [],
      program: [],
      level: [],
      stage: [],
      legalStatus: [],
      applicationRequirement: [],
      residency: [],
      searchAwardNameOrId: ''
    });

    this.searchAwardNameOrId = '';

    this.filteredPrograms = [];

    this.loadCompetitions();
    this.closeMobileFilters();
  }

  onPanelClosed(): void {
    if (this.pendingFilterChanges) {
      this.resetFormToAppliedState();
    }
  }

  isOptionSelected(optionId: number): boolean {
    return this.selectedTagIds.includes(optionId);
  }

  onSearchChange(event: any): void {
    console.log('Search change event:', event);
    const searchValue = event.target.value || '';
    this.searchAwardNameOrId = searchValue;
    this.form.get('searchAwardNameOrId').setValue(searchValue);
    console.log('Search text updated:', this.searchAwardNameOrId);
    this.pendingFilterChanges = true;
  }

  hasActiveFilters(): boolean {
    const hasSelectedTags = this.selectedTagIds.length > 0;
    const hasSearchText = this.searchAwardNameOrId && this.searchAwardNameOrId.trim().length > 0;
    return hasSelectedTags || hasSearchText;
  }

  getActiveFilterCount(): number {
    let count = 0;

    count += this.selectedTagIds.length;

    if (this.searchAwardNameOrId && this.searchAwardNameOrId.trim().length > 0) {
      count++;
    }

    return count;
  }

  goToFirstPage(): void {
    this.currentPage = 0;
    this.loadCompetitions();
  }

  goToLastPage(): void {
    const lastPage = Math.ceil(this.totalCount / this.pageSize) - 1;
    this.currentPage = Math.max(0, lastPage);
    this.loadCompetitions();
  }

  updateCompetitionsLanguage(): void {
    console.log('Updating competitions language for:', this.selectedLang);
    moment.locale(this.selectedLang);
    let dateFormat = 'MMMM DD, YYYY';
    if (this.selectedLang === 'fr') {
      dateFormat = 'DD MMMM YYYY';
    }
    this.competitions = this.competitions.map((competition) => {
      let formattedOpenDate = '';
      let formattedDeadline = '';
      if (competition.openDate) {
        formattedOpenDate = moment.utc(competition.openDate).tz(moment.tz.guess()).format(dateFormat);
      }
      if (competition.deadline) {
        formattedDeadline = moment.utc(competition.deadline).tz(moment.tz.guess()).format(dateFormat);
      }
      return {
        ...competition,
        formattedOpenDate,
        formattedDeadline,
        displayProgramName: competition.programFullName?.[this.selectedLang] || competition.programFullName?.['en'] || 'N/A',
        displayCompetitionName: competition.competitionFullName?.[this.selectedLang] || competition.competitionFullName?.['en'] || 'N/A',
        displayUrl: competition.competitionUrl?.[this.selectedLang] || competition.competitionUrl?.['en'] || ''
      };
    });
    console.log('Competitions language update completed');
  }

  setLanguage(lang: any): void {
    this.translate.use(lang.id);
  }

  onAwardDetailsClick(competition: any, event: Event): void {
    if (!competition.displayUrl || competition.displayUrl.trim() === '') {
      event.preventDefault();
      this.eCaseSnackBarService.show('failure', 'Award Details not available');
    }
  }

  populateAllFields(): void {
    const configs = this.getAllFieldConfigs();
    configs.forEach((config) => {
      this.mapTermsById(this.taxonomyTerms, config.id, this.selectedLang, config.property, config.name);
    });
  }

  updateAllFields(): void {
    if (this.taxonomyTerms?.length) {
      const configs = this.getAllFieldConfigs();
      configs.forEach((config) => {
        this.mapTermsById(this.taxonomyTerms, config.id, this.selectedLang, config.property);
      });
    }
  }

  getMin(a: number, b: number): number {
    return Math.min(a, b);
  }

  getCeil(a: number): number {
    return Math.ceil(a);
  }

  toggleMobileFilters(): void {
    this.isMobileFiltersExpanded = !this.isMobileFiltersExpanded;

    if (this.isMobileFiltersExpanded) {
      this.openMobileFilters();
    } else {
      this.closeMobileFilters();
    }
  }

  openMobileFilters(): void {
    this.isMobileFiltersExpanded = true;
    document.body.classList.add('filters-expanded');
  }

  closeMobileFilters(): void {
    this.isMobileFiltersExpanded = false;
    document.body.classList.remove('filters-expanded');
  }

  private removeExistingFilterValues(filterType: string): void {
    let filterIds: number[] = [];

    switch (filterType) {
      case 'faculty':
        filterIds = this.faculties.map(f => f.id);
        break;
      case 'program':
        filterIds = this.allPrograms.map(p => p.id);
        break;
      case 'level':
        filterIds = this.levels.map(l => l.id);
        break;
      case 'stage':
        filterIds = this.stages.map(s => s.id);
        break;
      case 'legalStatus':
        filterIds = this.legalStatuses.map(s => s.id);
        break;
      case 'applicationRequirement':
        filterIds = this.applicationRequirements.map(r => r.id);
        break;
      case 'residency':
        filterIds = this.residencies.map(r => r.id);
        break;
    }

    this.selectedTagIds = this.selectedTagIds.filter(id => !filterIds.includes(id));
  }

  private resetFormToAppliedState(): void {
    this.selectedTagIds = [...this.originalAppliedTagIds];
    this.searchAwardNameOrId = this.originalAppliedSearchText;
    this.form.get('searchAwardNameOrId').setValue(this.searchAwardNameOrId);
    this.pendingFilterChanges = false;

    this.resetFormToMatchSelectedTags();
  }

  private resetFormToMatchSelectedTags(): void {
    const formValues: any = {
      faculty: [],
      program: [],
      level: [],
      stage: [],
      legalStatus: [],
      applicationRequirement: [],
      residency: [],
      searchAwardNameOrId: this.searchAwardNameOrId
    };

    this.selectedTagIds.forEach((tagId) => {
      if (this.faculties.find(f => f.id === tagId)) {
        formValues.faculty.push(tagId);
      } else if (this.allPrograms.find(p => p.id === tagId)) {
        formValues.program.push(tagId);
      } else if (this.levels.find(l => l.id === tagId)) {
        formValues.level.push(tagId);
      } else if (this.stages.find(s => s.id === tagId)) {
        formValues.stage.push(tagId);
      } else if (this.legalStatuses.find(s => s.id === tagId)) {
        formValues.legalStatus.push(tagId);
      } else if (this.applicationRequirements.find(r => r.id === tagId)) {
        formValues.applicationRequirement.push(tagId);
      } else if (this.residencies.find(r => r.id === tagId)) {
        formValues.residency.push(tagId);
      }
    });

    this.form.patchValue(formValues);

    this.updateProgramOptions();
  }

  private getAllFieldConfigs(): any[] {
    return [
      {
        id: 1600,
        property: 'faculties',
        name: 'Faculties',
        labelProperty: 'facultyLabel',
        tooltipProperty: 'facultyLabelTooltip'
      },
      {
        id: 1601,
        property: 'allPrograms',
        name: 'Programs',
        labelProperty: 'programLabel',
        tooltipProperty: 'programLabelTooltip'
      },
      {
        id: 1602,
        property: 'levels',
        name: 'Levels',
        labelProperty: 'levelLabel',
        tooltipProperty: 'levelLabelTooltip'
      },
      {
        id: 1603,
        property: 'stages',
        name: 'Stages',
        labelProperty: 'stageLabel',
        tooltipProperty: 'stageLabelTooltip'
      },
      {
        id: 1604,
        property: 'legalStatuses',
        name: 'Legal Statuses',
        labelProperty: 'legalStatusLabel',
        tooltipProperty: 'legalStatusLabelTooltip'
      },
      {
        id: 1605,
        property: 'applicationRequirements',
        name: 'Application Requirements',
        labelProperty: 'applicationRequirementLabel',
        tooltipProperty: 'applicationRequirementLabelTooltip'
      },
      {
        id: 1606,
        property: 'residencies',
        name: 'Residencies',
        labelProperty: 'residencyLabel',
        tooltipProperty: 'residencyLabelTooltip'
      },
      {
        id: 1607,
        property: 'accessOptions',
        name: 'Access Options',
        labelProperty: 'accessLabel',
        tooltipProperty: 'accessLabelTooltip'
      },
      {
        id: 1608,
        property: 'miscOptions',
        name: 'Misc Options',
        labelProperty: 'miscLabel',
        tooltipProperty: 'miscLabelTooltip'
      }
    ];
  }

  private getHelpTooltip(metadata: any[], lang: string): string {
    if (metadata && metadata.length > 0) {
      const helpMetadata = metadata.find(item => item.metadataKey === 'HELP');
      if (helpMetadata && helpMetadata.label) {
        return helpMetadata.label[lang] || helpMetadata.label['en'] || '';
      }
    }
    return '';
  }

  private updateProgramOptions(): void {
    const selectedFacultyIds = this.form.get('faculty').value || [];

    if (selectedFacultyIds.length === 0) {
      this.filteredPrograms = [];
      this.form.get('program').setValue([]);
      this.selectedTagIds = this.selectedTagIds.filter(tagId =>
        !this.allPrograms.some(program => program.id === tagId)
      );
      return;
    }

    const relatedTermIds = new Set<number>();
    selectedFacultyIds.forEach((facultyId) => {
      const faculty = this.faculties.find(f => f.id === facultyId);
      if (faculty && faculty.relatedTermIds) {
        faculty.relatedTermIds.forEach(termId => relatedTermIds.add(termId));
      }
    });

    this.filteredPrograms = this.allPrograms.filter(program => relatedTermIds.has(program.id));

    const currentProgramSelection = this.form.get('program').value || [];
    const validPrograms = currentProgramSelection.filter(programId =>
      this.filteredPrograms.some(program => program.id === programId)
    );

    if (validPrograms.length !== currentProgramSelection.length) {
      this.form.get('program').setValue(validPrograms);
      this.selectedTagIds = this.selectedTagIds.filter(tagId =>
        validPrograms.includes(tagId) || !this.allPrograms.some(program => program.id === tagId)
      );
    }
  }

  private mapTermsById(response: any, id: number, lang: string, property: string, name?: string): void {
    const object = response.find(item => item.id === id);
    if (object?.children) {
      const config = this.getAllFieldConfigs().find(c => c.id === id);
      if (config?.labelProperty) {
        this[config.labelProperty] = object.label[lang] || object.label['en'];
      }
      if (config?.tooltipProperty) {
        this[config.tooltipProperty] = this.getHelpTooltip(object.metadata, lang);
      }

      const mappedChildren = object.children.map(child => ({
        id: child.id,
        label: child.label[lang] || child.label['en'],
        clientCode: child.clientCode,
        value: child.value,
        tooltip: this.getHelpTooltip(child.metadata, lang),
        relatedTermIds: child.relatedTermIds || []
      })).sort((a, b) => {
        const labelA = a.label.toLowerCase();
        const labelB = b.label.toLowerCase();
        return labelA.localeCompare(labelB);
      });

      this[property] = mappedChildren;

      if (property === 'allPrograms') {
        this.allPrograms = [...mappedChildren];
        this.filteredPrograms = [];
      }

      if (name) {
        console.log(`${name} populated:`, this[property]);
      }
    }
  }
}
